import type { Artifact } from "./types";

/**
 * The bar chart's sort toggle (VIZ-2): a reader's view, never the data's.
 *
 * The server sorts a chart the way its SQL said to, and that order is what is
 * stored, exported and printed. Re-ordering by value here changes only what
 * one reader sees on screen, for the one question "which is biggest?" — it is
 * not persisted, and a printed page never has the toggle at all.
 */

const SORTABLE = new Set(["bar_chart", "horizontal_bar_chart", "lollipop_chart"]);

export function canSortByValue(artifact: Artifact): boolean {
  const chart = artifact.chart;
  return (
    SORTABLE.has(artifact.type) &&
    (chart?.series?.length ?? 0) === 1 &&
    (chart?.categories?.length ?? 0) > 2
  );
}

/** The same chart with its categories ordered largest first. */
export function sortedByValue(artifact: Artifact): Artifact {
  const chart = artifact.chart;
  const [series] = chart?.series ?? [];
  if (!chart || !series) return artifact;
  const values = series.values ?? [];
  const order = (chart.categories ?? [])
    .map((_, index) => index)
    .sort((left, right) => {
      const a = values[left];
      const b = values[right];
      if (a === null || a === undefined) return 1;
      if (b === null || b === undefined) return -1;
      return b - a;
    });
  const pick = <T,>(list: T[] | null | undefined) => (list ? order.map((index) => list[index]!) : list);
  return {
    ...artifact,
    chart: {
      ...chart,
      categories: pick(chart.categories) ?? [],
      series: [
        {
          ...series,
          values: pick(series.values) ?? [],
          lower: pick(series.lower),
          upper: pick(series.upper),
        },
      ],
    },
  };
}
